import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import clsx from 'clsx';
import { Info, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { usePeriod, type Period } from '../lib/period';
import { Badge, Card, ErrorState, LoadingBlock, PageHeader, ProgressBar, Table, Tabs, Td, Th, Tr } from '../components/ui';
import { Delta, KpiCard, MoneyValue, PeriodLine, Section, TrustBadge, useMoney, useNumber } from '../components/kit';
import { CurrencyAmounts, DayRange, Fact, hrefWith, NotGuaranteed, useDay, useMonthName, usePercent } from '../components/a-common';
import {
  AGING_BUCKETS,
  CoverageBadge,
  FinanceTrust,
  isSuppressed,
  lineOf,
  NoticeList,
  rowsFromMonths,
  rowsFromTrends,
  SUBSCRIPTION_AGING,
  useTrustHint,
  type CashStock,
  type Compared,
  type ContractsSubscriptions,
  type FinanceExpenses,
  type FinanceOverview,
  type FinanceTrends,
  type MaybeSuppressed,
  type ReceivablesFull,
  type TrendRow,
} from '../components/a-finance';
import { MonthTrend, StartedCancelled, ValueBars } from '../components/a-charts';

/**
 * Financial Intelligence (CR-008 §5.3, §10): Finance's own figures, never a second set of books. Revenue
 * is cash collected; invoices issued are their own line; receivables are owed as of today; the net result
 * is Finance's cash-basis management P&L. Every figure keeps Finance's trust status, its coverage and its
 * notices, and money is never added across currencies here. Read-only.
 */

const TABS = ['overview', 'trends', 'receivables', 'expenses', 'contracts', 'trust'] as const;
type Tab = (typeof TABS)[number];

export function FinancePage() {
  const { t } = useI18n();
  const { param, setParam } = usePeriod();
  const asked = param('tab');
  const tab: Tab = (TABS as readonly string[]).includes(asked) ? (asked as Tab) : 'overview';
  return (
    <>
      <PageHeader title={t('finance.title')} subtitle={t('finance.subtitle')} />
      <Tabs tabs={TABS.map((id) => ({ id, label: t(`finance.tab.${id}`) }))} active={tab} onChange={(id) => setParam('tab', id === 'overview' ? null : id)} />
      <div className="mt-5">
        {tab === 'overview' && <OverviewTab />}
        {tab === 'trends' && <TrendsTab />}
        {tab === 'receivables' && <ReceivablesTab />}
        {tab === 'expenses' && <ExpensesTab />}
        {tab === 'contracts' && <ContractsTab />}
        {tab === 'trust' && <TrustTab />}
      </div>
    </>
  );
}

// ---- what every tab shares ---------------------------------------------------------------------------

/** A tab's figures once they are here: loading, or the error with a retry, until then. */
function Loaded<T>({ query, children }: { query: UseQueryResult<T, Error>; children: (data: T) => ReactNode }) {
  const { t } = useI18n();
  if (query.isLoading) return <LoadingBlock />;
  if (query.error || query.data === undefined || query.data === null) {
    return (
      <Card>
        <ErrorState error={query.error ?? t('common.error')} onRetry={() => void query.refetch()} />
      </Card>
    );
  }
  return <>{children(query.data)}</>;
}

function useFinanceOverview() {
  const { params } = usePeriod();
  return useQuery({
    queryKey: ['executive', 'finance', 'overview', params],
    queryFn: () => api.get<FinanceOverview & { period: Period }>('/executive/finance/overview', { ...params }),
  });
}

/** "As of today" — for the tabs the period picker does not change. */
function AsOfToday({ date, children }: { date: string; children?: ReactNode }) {
  const { t } = useI18n();
  const day = useDay();
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-body-sm">
        <Badge tone="info">{t('finance.asOfBadge', { date: day(date) })}</Badge>
        <span className="text-caption text-muted">{t('finance.periodDoesNotApply')}</span>
      </div>
      {children && <div className="mt-3">{children}</div>}
    </Card>
  );
}

/** Salaries or other expenses: the figure, or why it is not shown. */
function SplitCard({ label, value, hint, basis, goodWhenDown = true, to }: { label: string; value: MaybeSuppressed<Compared>; hint?: string; basis: string; goodWhenDown?: boolean; to?: string }) {
  const { t } = useI18n();
  const money = useMoney();
  if (isSuppressed(value)) {
    return <KpiCard label={label} value={<span className="text-body text-muted">{t('state.suppressed')}</span>} hint={t('state.suppressedSmallCohort', { n: value.minimum })} />;
  }
  return (
    <KpiCard
      label={label}
      value={<MoneyValue line={value.current} />}
      delta={<Delta current={value.current.base} previous={value.previous.base} basis={basis} goodWhenDown={goodWhenDown} format={(n) => money(n, value.current.currency)} />}
      hint={hint}
      to={to}
    />
  );
}

function CashCard({ cash }: { cash: CashStock }) {
  const { t } = useI18n();
  const day = useDay();
  const hint = useTrustHint();
  return (
    <div className="surface h-full rounded-lg p-4">
      <div className="flex items-start justify-between gap-2">
        <span className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{t('finance.card.cash')}</span>
        <TrustBadge status={cash.status} />
      </div>
      {cash.byCurrency ? (
        <div className="mt-2 text-title text-[var(--text-primary)]">
          <CurrencyAmounts amounts={cash.byCurrency} empty={<span className="text-body-sm text-muted">{t('finance.cashNone')}</span>} />
        </div>
      ) : (
        <p className="mt-2 flex items-center gap-2 text-body-sm text-muted">
          {cash.status === 'RESTRICTED' && <Lock className="h-4 w-4" aria-hidden />}
          {hint(cash.status)}
        </p>
      )}
      <p className="mt-1 text-caption text-muted">{t('finance.asOfDate', { date: day(cash.asOf) })}</p>
      <p className="mt-0.5 text-caption text-[var(--text-tertiary)]">{t('finance.cashHint')}</p>
    </div>
  );
}

/** A month table: what was collected, spent and left, with Finance's coverage of each month. */
function MonthsTable({ rows, currency, total }: { rows: TrendRow[]; currency: string; total?: { revenue: ReactNode; expenses: ReactNode; result: ReactNode } }) {
  const { t } = useI18n();
  const money = useMoney();
  const monthName = useMonthName();
  const cell = (v: number | null) => (v === null ? <span className="text-muted">{t('common.unknown')}</span> : money(v, currency));
  if (!rows.length) return <p className="text-body-sm text-muted">{t('state.noData')}</p>;
  return (
    <Table>
      <thead>
        <tr>
          <Th>{t('finance.month')}</Th>
          <Th align="end">{t('finance.collected')}</Th>
          <Th align="end">{t('finance.expenses')}</Th>
          <Th align="end">{t('finance.result')}</Th>
          <Th>{t('finance.coverage')}</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <Tr key={r.month}>
            <Td className="whitespace-nowrap">{monthName(r.month)}</Td>
            <Td align="end" className="tabular">{cell(r.revenue)}</Td>
            <Td align="end" className="tabular">{cell(r.expenses)}</Td>
            <Td align="end" className={clsx('tabular', r.result !== null && r.result < 0 && 'text-error-fg dark:text-error-dark')}>{cell(r.result)}</Td>
            <Td>
              <span className="inline-flex flex-wrap items-center gap-1">
                <CoverageBadge state={r.state} />
                {r.partialMonth && <span className="text-caption text-muted">{t('finance.partialMonth')}</span>}
              </span>
            </Td>
          </Tr>
        ))}
        {total && (
          <Tr className="font-medium">
            <Td>{t('common.total')}</Td>
            <Td align="end" className="tabular">{total.revenue}</Td>
            <Td align="end" className="tabular">{total.expenses}</Td>
            <Td align="end" className="tabular">{total.result}</Td>
            <Td />
          </Tr>
        )}
      </tbody>
    </Table>
  );
}

// ---- overview ----------------------------------------------------------------------------------------

function OverviewTab() {
  const { t } = useI18n();
  const { query } = usePeriod();
  const money = useMoney();
  const num = useNumber();
  const day = useDay();
  const pct = usePercent();
  const q = useFinanceOverview();
  return (
    <Loaded query={q}>
      {(d) => {
        const base = d.context.baseCurrency;
        const basis = d.period.comparison.basis;
        const asMoney = (n: number) => money(n, base);
        const rec = d.cards.receivables;
        const net = d.cards.netResult.current;
        const c = d.coverage;
        return (
          <div className="space-y-5">
            <Card>
              <PeriodLine period={d.period} asOf={d.context.generatedAt} />
              <div className="mt-3 border-t hairline pt-3">
                <FinanceTrust context={d.context} />
              </div>
            </Card>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <KpiCard
                label={t('finance.card.collected')}
                value={<MoneyValue line={d.cards.revenue.current} />}
                delta={<Delta current={d.cards.revenue.current.base} previous={d.cards.revenue.previous.base} basis={basis} format={asMoney} />}
                hint={t('finance.card.collectedHint')}
              />
              <KpiCard
                label={t('finance.card.invoiced')}
                value={<MoneyValue line={d.cards.invoicesIssued.current} />}
                delta={<Delta current={d.cards.invoicesIssued.current.base} previous={d.cards.invoicesIssued.previous.base} basis={basis} format={asMoney} />}
                hint={t('finance.card.invoicedHint', { n: num(d.cards.invoicesIssued.current.count) })}
              />
              <KpiCard
                label={t('finance.card.operatingExpenses')}
                value={<MoneyValue line={d.cards.operatingExpenses.current} />}
                delta={<Delta current={d.cards.operatingExpenses.current.base} previous={d.cards.operatingExpenses.previous.base} basis={basis} format={asMoney} goodWhenDown />}
                hint={t('metric.operatingExpensesHint')}
                to={hrefWith('/finance', query, { tab: 'expenses' })}
              />
              <KpiCard
                label={t('finance.card.netResult')}
                value={<MoneyValue line={net} />}
                delta={<Delta current={net.base} previous={d.cards.netResult.previous.base} basis={basis} format={asMoney} />}
                hint={t('metric.netResultHint')}
                tone={net.base !== null && net.base < 0 ? 'error' : 'neutral'}
              />
              <SplitCard label={t('finance.card.salaries')} value={d.cards.salaries} hint={t('finance.salariesHint')} basis={basis} to={hrefWith('/finance', query, { tab: 'expenses' })} />
              <SplitCard label={t('finance.card.otherExpenses')} value={d.cards.otherExpenses} basis={basis} to={hrefWith('/finance', query, { tab: 'expenses' })} />
              <KpiCard
                label={t('finance.card.outstanding')}
                value={<MoneyValue line={lineOf(rec.total, rec.currency, rec.complete, rec.unconverted)} />}
                hint={
                  rec.complete && rec.total === 0
                    ? t('metric.nothingOwed', { date: day(rec.asOf) })
                    : t('metric.receivablesHint', { date: day(rec.asOf), overdue: rec.complete ? money(rec.overdue, rec.currency) : t('state.partial'), share: pct(rec.overdueSharePercent) })
                }
                to={hrefWith('/finance', query, { tab: 'receivables' })}
              />
              <CashCard cash={d.cards.cash} />
            </div>

            <Section title={t('finance.months.title')} subtitle={t('finance.inBase', { currency: base })}>
              <MonthsTable rows={rowsFromMonths(d.months)} currency={base} />
            </Section>

            <Section title={t('finance.coverageSummary.title')}>
              {c.notProvided + c.overlapChanged + c.missingDocuments + c.drafts === 0 ? (
                <p className="text-body-sm text-muted">{t('finance.coverageSummary.allClear', { n: num(c.months) })}</p>
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {c.overlapChanged > 0 && (
                    <li>
                      <Badge tone="error">{t('finance.coverageSummary.overlapChanged', { n: num(c.overlapChanged) })}</Badge>
                    </li>
                  )}
                  {c.notProvided > 0 && (
                    <li>
                      <Badge tone="warning">{t('finance.coverageSummary.notProvided', { n: num(c.notProvided) })}</Badge>
                    </li>
                  )}
                  {c.missingDocuments > 0 && (
                    <li>
                      <Badge tone="warning">{t('finance.coverageSummary.missingDocuments', { n: num(c.missingDocuments) })}</Badge>
                    </li>
                  )}
                  {c.drafts > 0 && (
                    <li>
                      <Badge tone="neutral">{t('finance.coverageSummary.drafts', { n: num(c.drafts) })}</Badge>
                    </li>
                  )}
                </ul>
              )}
              <p className="mt-3 text-caption text-muted">
                <Link to={hrefWith('/finance', query, { tab: 'trust' })} className="text-primary-600 hover:underline dark:text-primary-400">
                  {t('finance.coverageSummary.open')}
                </Link>
              </p>
            </Section>
            <p className="text-caption text-muted">{t('finance.readOnly')}</p>
          </div>
        );
      }}
    </Loaded>
  );
}

// ---- trends ------------------------------------------------------------------------------------------

function TrendsTab() {
  const { t } = useI18n();
  const { params } = usePeriod();
  const monthName = useMonthName();
  const q = useQuery({
    queryKey: ['executive', 'finance', 'trends', params],
    queryFn: () => api.get<FinanceTrends>('/executive/finance/trends', { ...params }),
  });
  return (
    <Loaded query={q}>
      {(d) => {
        const base = d.context.baseCurrency;
        const rows = rowsFromTrends(d.rows);
        const first = d.rows[0]?.month;
        const last = d.rows[d.rows.length - 1]?.month;
        return (
          <div className="space-y-5">
            <Card>
              <FinanceTrust context={d.context} />
            </Card>
            <Section
              title={t('finance.trends.title')}
              subtitle={first && last ? t('finance.trends.subtitle', { from: monthName(first), to: monthName(last), currency: base }) : undefined}
            >
              <MonthTrend rows={rows} currency={base} />
            </Section>
            <Section title={t('finance.trends.table')} subtitle={t('finance.inBase', { currency: base })}>
              <MonthsTable
                rows={rows}
                currency={base}
                total={{
                  revenue: <MoneyValue line={d.total.revenue} compact />,
                  expenses: <MoneyValue line={d.total.expenses} compact />,
                  result: <MoneyValue line={d.total.result} compact />,
                }}
              />
            </Section>
          </div>
        );
      }}
    </Loaded>
  );
}

// ---- receivables -------------------------------------------------------------------------------------

function ReceivablesTab() {
  const { t, localized } = useI18n();
  const { query } = usePeriod();
  const money = useMoney();
  const num = useNumber();
  const pct = usePercent();
  const day = useDay();
  const q = useQuery({
    queryKey: ['executive', 'finance', 'receivables'],
    queryFn: () => api.get<ReceivablesFull>('/executive/finance/receivables'),
  });
  return (
    <Loaded query={q}>
      {(d) => {
        const complete = d.unconverted.length === 0;
        const bucketAmount = (b: string) => d.aging.find((a) => a.bucket === b)?.amount ?? 0;
        const overdueTotal = d.aging.filter((a) => a.bucket !== 'CURRENT').reduce((s, a) => s + a.amount, 0);
        const subscriptionsByCurrency = [...d.expected.subscriptionBilling.reduce((m, s) => m.set(s.currency, (m.get(s.currency) ?? 0) + s.amount), new Map<string, number>())].map(([currency, amount]) => ({ currency, amount }));
        const f = d.forecast;
        return (
          <div className="space-y-5">
            <AsOfToday date={d.asOf}>
              <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
                <div>
                  <p className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{t('finance.outstanding')}</p>
                  <p className="text-metric tabular text-[var(--text-primary)]">
                    <MoneyValue line={lineOf(d.total, d.currency, complete, d.unconverted)} />
                  </p>
                </div>
                <div>
                  <p className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{t('finance.recv.overdueCol')}</p>
                  <p className="text-title tabular text-error-fg dark:text-error-dark">
                    <MoneyValue line={lineOf(overdueTotal, d.currency, complete, d.unconverted)} compact />
                  </p>
                  <p className="text-caption text-muted">{t('finance.recv.overdueInvoices', { n: num(d.overdueCount) })}</p>
                </div>
                {d.concentration && <p className="max-w-md text-body-sm text-muted">{t('finance.recv.concentration', { client: localized(d.concentration.client), share: pct(d.concentration.sharePercent) })}</p>}
              </div>
            </AsOfToday>

            <div className="grid gap-4 xl:grid-cols-2">
              <Section title={t('finance.recv.aging')} subtitle={t('finance.inBase', { currency: d.currency })}>
                <ValueBars
                  rows={AGING_BUCKETS.map((b) => ({
                    key: b,
                    label: t(`finance.recv.bucket.${b}`),
                    value: bucketAmount(b),
                    tone: b === 'CURRENT' ? ('success' as const) : b === 'D1_30' ? ('warning' as const) : ('error' as const),
                  }))}
                  format={(v) => money(v, d.currency)}
                />
                {!complete && <p className="mt-3 text-caption text-warning-fg dark:text-warning-dark">{t('state.unconverted', { currencies: d.unconverted.join(', ') })}</p>}
              </Section>

              <Section title={t('finance.recv.byClient')} subtitle={t('finance.inBase', { currency: d.currency })}>
                {d.byClient.length === 0 ? (
                  <p className="text-body-sm text-muted">{t('finance.recv.nothingOwed')}</p>
                ) : (
                  <Table>
                    <thead>
                      <tr>
                        <Th>{t('common.client')}</Th>
                        <Th align="end">{t('finance.outstanding')}</Th>
                        <Th align="end">{t('finance.recv.overdueCol')}</Th>
                        <Th>{t('finance.recv.currencies')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.byClient.map((c) => (
                        <Tr key={c.client.id}>
                          <Td>
                            <Link to={hrefWith(`/clients/${c.client.id}`, query)} className="text-primary-600 hover:underline dark:text-primary-400">
                              {localized(c.client)}
                            </Link>
                          </Td>
                          <Td align="end" className="tabular">{money(c.total, d.currency)}</Td>
                          <Td align="end" className={clsx('tabular', c.overdue > 0 && 'text-error-fg dark:text-error-dark')}>{money(c.overdue, d.currency)}</Td>
                          <Td>
                            <span className="font-mono text-mono-sm text-muted" dir="ltr">
                              {c.currencies.join(' ')}
                            </span>
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Section>
            </div>

            <Section title={t('finance.recv.overdueList')} subtitle={d.overdueCount > d.overdue.length ? t('finance.recv.shownOf', { shown: num(d.overdue.length), total: num(d.overdueCount) }) : undefined}>
              {d.overdue.length === 0 ? (
                <p className="text-body-sm text-muted">{t('finance.recv.noneOverdue')}</p>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('finance.recv.invoice')}</Th>
                      <Th>{t('common.client')}</Th>
                      <Th>{t('common.project')}</Th>
                      <Th>{t('finance.recv.dueDate')}</Th>
                      <Th align="end">{t('finance.recv.daysOverdue')}</Th>
                      <Th align="end">{t('finance.recv.outstandingOriginal')}</Th>
                      <Th align="end">{t('finance.recv.outstandingBase', { currency: d.currency })}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.overdue.map((inv) => (
                      <Tr key={inv.id}>
                        <Td>
                          <span className="font-mono text-mono-sm" dir="ltr">
                            {inv.number}
                          </span>
                        </Td>
                        <Td>
                          {inv.client ? (
                            <Link to={hrefWith(`/clients/${inv.client.id}`, query)} className="hover:underline">
                              {localized(inv.client)}
                            </Link>
                          ) : (
                            '—'
                          )}
                        </Td>
                        <Td>
                          {inv.project ? (
                            <Link to={hrefWith(`/projects/${inv.project.id}`, query)} className="hover:underline">
                              <span className="font-mono text-mono-sm" dir="ltr">
                                {inv.project.code}
                              </span>{' '}
                              {localized(inv.project)}
                            </Link>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </Td>
                        <Td className="whitespace-nowrap">{day(inv.dueDate)}</Td>
                        <Td align="end" className="tabular text-error-fg dark:text-error-dark">{num(inv.daysOverdue)}</Td>
                        <Td align="end" className="tabular">{money(inv.remaining, inv.currency)}</Td>
                        <Td align="end" className="tabular">{inv.remainingBase === null ? <span className="text-warning-fg dark:text-warning-dark">{t('finance.recv.noRate')}</span> : money(inv.remainingBase, d.currency)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Section>

            <Section title={t('finance.expected.title')} subtitle={t('finance.expected.subtitle')}>
              <div className="grid gap-4 lg:grid-cols-3">
                <ExpectedBucket title={t('finance.expected.overdue')} hint={t('finance.expected.overdueHint')}>
                  <p className="text-title tabular text-[var(--text-primary)]">{money(d.expected.overdueCollectible, d.currency)}</p>
                </ExpectedBucket>
                <ExpectedBucket title={t('finance.expected.scheduled')} hint={t('finance.expected.scheduledHint')}>
                  <dl className="space-y-1 text-body-sm">
                    {(
                      [
                        ['finance.expected.in7', d.expected.dueNext7],
                        ['finance.expected.in30', d.expected.dueNext30],
                        ['finance.expected.in90', d.expected.dueNext90],
                      ] as Array<[TranslationKey, number]>
                    ).map(([label, v]) => (
                      <div key={label} className="flex items-baseline justify-between gap-3">
                        <dt className="text-[var(--text-tertiary)]">{t(label)}</dt>
                        <dd className="tabular text-[var(--text-primary)]">{money(v, d.currency)}</dd>
                      </div>
                    ))}
                  </dl>
                </ExpectedBucket>
                <ExpectedBucket title={t('finance.expected.billing')} hint={t('finance.expected.billingHint')}>
                  <dl className="space-y-2 text-body-sm">
                    <div>
                      <dt className="text-[var(--text-tertiary)]">{t('finance.expected.contractsDue')}</dt>
                      <dd className="tabular">
                        <CurrencyAmounts amounts={d.expected.contractBilling.dueNotInvoiced} empty={<span className="text-muted">{t('finance.expected.none')}</span>} />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[var(--text-tertiary)]">{t('finance.expected.contractsUpcoming', { days: num(d.expected.contractBilling.horizonDays) })}</dt>
                      <dd className="tabular">
                        <CurrencyAmounts amounts={d.expected.contractBilling.upcoming} empty={<span className="text-muted">{t('finance.expected.none')}</span>} />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[var(--text-tertiary)]">{t('finance.expected.subscriptions', { n: num(d.expected.subscriptionBilling.length) })}</dt>
                      <dd className="tabular">
                        <CurrencyAmounts amounts={subscriptionsByCurrency} empty={<span className="text-muted">{t('finance.expected.none')}</span>} />
                      </dd>
                    </div>
                  </dl>
                </ExpectedBucket>
              </div>
              {!complete && <p className="mt-3 text-caption text-warning-fg dark:text-warning-dark">{t('finance.expected.noRateLeftOut', { currencies: d.unconverted.join(', ') })}</p>}
            </Section>

            <Section title={t('finance.forecast.title')} subtitle={f.available ? t('finance.forecast.estimate') : undefined}>
              {f.available ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-baseline gap-3">
                    <span className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{t('finance.forecast.expected30')}</span>
                    <span className="text-title tabular text-[var(--text-primary)]">{money(f.expectedNext30, f.currency)}</span>
                    <NotGuaranteed />
                  </div>
                  <dl className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <Fact label={t('finance.forecast.median')}>
                      <DelayText days={f.medianDaysLate} />
                    </Fact>
                    <Fact label={t('finance.forecast.p75')}>
                      <DelayText days={f.p75DaysLate} />
                    </Fact>
                    <Fact label={t('finance.forecast.paidByDue')}>{pct(f.paidByDueDatePercent)}</Fact>
                    <Fact label={t('finance.forecast.confidence')}>{t(`finance.confidence.${f.confidence}`)}</Fact>
                    <Fact label={t('finance.forecast.sample')}>{t('finance.forecast.sampleValue', { n: num(f.sample) })}</Fact>
                    <Fact label={t('finance.forecast.window')}>
                      <DayRange start={f.window.from} last={f.window.to} />
                    </Fact>
                    <Fact label={t('finance.forecast.method')} className="sm:col-span-2">
                      {t('finance.forecast.methodText')}
                    </Fact>
                  </dl>
                </div>
              ) : (
                <p className="flex items-start gap-2 text-body-sm text-muted">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  {t('finance.forecast.notEnough', { sample: num(f.sample), minimum: num(f.minimumSample) })}
                </p>
              )}
            </Section>
          </div>
        );
      }}
    </Loaded>
  );
}

function ExpectedBucket({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <div className="surface-raised flex flex-col gap-2 rounded-md p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="text-title text-[var(--text-primary)]">{title}</h4>
        <NotGuaranteed />
      </div>
      {children}
      <p className="mt-auto text-caption text-muted">{hint}</p>
    </div>
  );
}

/** Days from the due date to payment: after, before, or on the day. */
function DelayText({ days }: { days: number | null }) {
  const { t } = useI18n();
  const num = useNumber();
  if (days === null) return <>—</>;
  if (days === 0) return <>{t('finance.forecast.onTheDay')}</>;
  return <>{days > 0 ? t('finance.forecast.daysLate', { n: num(days) }) : t('finance.forecast.daysEarly', { n: num(Math.abs(days)) })}</>;
}

// ---- expenses ----------------------------------------------------------------------------------------

function ExpensesTab() {
  const { t, localized } = useI18n();
  const { params } = usePeriod();
  const money = useMoney();
  const num = useNumber();
  const pct = usePercent();
  const day = useDay();
  const q = useQuery({
    queryKey: ['executive', 'finance', 'expenses', params],
    queryFn: () => api.get<FinanceExpenses>('/executive/finance/expenses', { ...params }),
  });
  return (
    <Loaded query={q}>
      {(d) => {
        const base = d.context.baseCurrency;
        const basis = d.period.comparison.basis;
        const hidden = isSuppressed(d.salaries) || isSuppressed(d.other);
        const minimum = isSuppressed(d.salaries) ? d.salaries.minimum : isSuppressed(d.other) ? d.other.minimum : 0;
        const categories = [...d.byCategory].sort((a, b) => (b.amount.base ?? -Infinity) - (a.amount.base ?? -Infinity));
        const historicalNote = (line: { historical: { base: number | null; count: number } }) =>
          line.historical.count > 0 ? t('finance.exp.historical', { amount: line.historical.base === null ? t('state.partial') : money(line.historical.base, base) }) : undefined;
        return (
          <div className="space-y-5">
            <Card>
              <PeriodLine period={d.period} />
              <div className="mt-3 border-t hairline pt-3">
                <FinanceTrust context={d.context} />
              </div>
            </Card>

            <div className="grid gap-4 sm:grid-cols-3">
              <KpiCard
                label={t('finance.exp.total')}
                value={<MoneyValue line={d.total.current} />}
                delta={<Delta current={d.total.current.base} previous={d.total.previous.base} basis={basis} goodWhenDown format={(n) => money(n, base)} />}
                hint={t('metric.operatingExpensesHint')}
              />
              {isSuppressed(d.salaries) ? (
                <KpiCard label={t('finance.card.salaries')} value={<span className="text-body text-muted">{t('state.suppressed')}</span>} hint={t('state.suppressedSmallCohort', { n: d.salaries.minimum })} />
              ) : (
                <KpiCard label={t('finance.card.salaries')} value={<MoneyValue line={d.salaries.current} />} hint={historicalNote(d.salaries) ?? t('finance.salariesHint')} />
              )}
              {isSuppressed(d.other) ? (
                <KpiCard label={t('finance.card.otherExpenses')} value={<span className="text-body text-muted">{t('state.suppressed')}</span>} hint={t('state.suppressedSmallCohort', { n: d.other.minimum })} />
              ) : (
                <KpiCard label={t('finance.card.otherExpenses')} value={<MoneyValue line={d.other.current} />} hint={historicalNote(d.other)} />
              )}
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <Section title={t('finance.exp.split')} subtitle={t('finance.inBase', { currency: base })}>
                {isSuppressed(d.salaries) || isSuppressed(d.other) ? (
                  <p className="text-body-sm text-muted">{t('state.suppressedSmallCohort', { n: minimum })}</p>
                ) : (
                  <ValueBars
                    rows={[
                      { key: 'salaries', label: t('finance.card.salaries'), value: d.salaries.current.base, display: <MoneyValue line={d.salaries.current} compact /> },
                      { key: 'other', label: t('finance.card.otherExpenses'), value: d.other.current.base, display: <MoneyValue line={d.other.current} compact /> },
                    ]}
                    format={(v) => money(v, base)}
                  />
                )}
              </Section>

              <Section title={t('finance.exp.byCategory')} subtitle={t('finance.inBase', { currency: base })}>
                {hidden ? (
                  <p className="text-body-sm text-muted">{t('finance.exp.categoriesHidden', { n: minimum })}</p>
                ) : categories.length === 0 ? (
                  <p className="text-body-sm text-muted">{t('finance.exp.noCategories')}</p>
                ) : (
                  <ValueBars
                    rows={categories.map((c, i) => ({
                      key: c.category?.id ?? `none-${i}`,
                      label: c.category ? localized(c.category) : t('finance.exp.uncategorised'),
                      value: c.amount.base,
                      display: <MoneyValue line={c.amount} compact />,
                    }))}
                    format={(v) => money(v, base)}
                  />
                )}
              </Section>
            </div>

            <Section title={t('finance.exp.budgets')} subtitle={t('finance.exp.budgetsHint')}>
              {d.budgets.length === 0 ? (
                <p className="text-body-sm text-muted">{t('finance.exp.noBudgets')}</p>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('finance.exp.budget')}</Th>
                      <Th>{t('finance.exp.dates')}</Th>
                      <Th align="end">{t('finance.exp.amount')}</Th>
                      <Th align="end">{t('finance.exp.spent')}</Th>
                      <Th>{t('finance.exp.used')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.budgets.map((b) => (
                      <Tr key={b.id}>
                        <Td>{b.name}</Td>
                        <Td>
                          <span className="whitespace-nowrap text-caption text-muted">
                            {day(b.startDate)} – {day(b.endDate)}
                          </span>
                        </Td>
                        <Td align="end" className="tabular">{money(b.amount, b.currency)}</Td>
                        <Td align="end" className="tabular">{money(b.spent, b.currency)}</Td>
                        <Td className="min-w-[10rem]">
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <ProgressBar percent={b.percentUsed} tone={b.limitExceeded ? 'error' : b.thresholdExceeded ? 'warning' : 'success'} />
                            </div>
                            <span className="tabular text-caption">{pct(b.percentUsed)}</span>
                          </div>
                          {(b.limitExceeded || b.thresholdExceeded) && (
                            <span className={clsx('text-caption', b.limitExceeded ? 'text-error-fg dark:text-error-dark' : 'text-warning-fg dark:text-warning-dark')}>
                              {b.limitExceeded ? t('finance.exp.overLimit') : t('finance.exp.overThreshold')}
                            </span>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
              {d.budgets.length > 0 && <p className="mt-2 text-caption text-muted">{t('finance.exp.budgetCount', { n: num(d.budgets.length) })}</p>}
            </Section>
          </div>
        );
      }}
    </Loaded>
  );
}

// ---- contracts & subscriptions -------------------------------------------------------------------------

function ContractsTab() {
  const { t, localized, locale } = useI18n();
  const { params, query } = usePeriod();
  const money = useMoney();
  const num = useNumber();
  const day = useDay();
  const q = useQuery({
    queryKey: ['executive', 'finance', 'contracts', params],
    queryFn: () => api.get<ContractsSubscriptions>('/executive/finance/contracts-subscriptions', { ...params }),
  });
  return (
    <Loaded query={q}>
      {(d) => {
        const s = d.subscriptions;
        const c = d.contracts;
        const base = s.recurring.base;
        const recurringComplete = base.unconvertedCurrencies.length === 0;
        const yearStart = `${d.asOf.slice(0, 4)}-01-01`;
        return (
          <div className="space-y-6">
            <AsOfToday date={d.asOf}>
              <p className="flex items-start gap-2 text-caption text-muted">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                {t('finance.contracts.notCollected')}
              </p>
            </AsOfToday>

            <section className="space-y-4" aria-labelledby="subs-title">
              <h2 id="subs-title" className="text-h2 text-[var(--text-primary)]">
                {t('finance.subs.title')}
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <KpiCard
                  label={t('finance.subs.mrr')}
                  value={recurringComplete ? money(base.mrr, base.currency) : <MoneyValue line={lineOf(base.mrr, base.currency, false, base.unconvertedCurrencies)} />}
                  hint={t('finance.subs.contractedHint')}
                />
                <KpiCard
                  label={t('finance.subs.arr')}
                  value={recurringComplete ? money(base.arr, base.currency) : <MoneyValue line={lineOf(base.arr, base.currency, false, base.unconvertedCurrencies)} />}
                  hint={t('finance.subs.contractedHint')}
                />
                <KpiCard
                  label={t('finance.subs.running')}
                  value={num(s.counts.active + s.counts.pastDue)}
                  hint={t('finance.subs.runningHint', { active: num(s.counts.active), pastDue: num(s.counts.pastDue), ending: num(s.counts.ending) })}
                  tone={s.counts.pastDue > 0 ? 'warning' : 'neutral'}
                />
                <KpiCard
                  label={t('finance.subs.cycles')}
                  value={t('finance.subs.cyclesValue', { monthly: num(s.counts.monthly), yearly: num(s.counts.yearly) })}
                  hint={t('finance.subs.cancelledHint', { n: num(s.counts.cancelled), total: num(s.counts.total) })}
                />
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <Section title={t('finance.subs.byCurrency')} subtitle={t('finance.subs.contracted')}>
                  {s.recurring.byCurrency.length === 0 ? (
                    <p className="text-body-sm text-muted">{t('finance.subs.none')}</p>
                  ) : (
                    <Table>
                      <thead>
                        <tr>
                          <Th>{t('common.currency')}</Th>
                          <Th align="end">{t('finance.subs.subscriptions')}</Th>
                          <Th align="end">{t('finance.subs.mrr')}</Th>
                          <Th align="end">{t('finance.subs.arr')}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.recurring.byCurrency.map((r) => (
                          <Tr key={r.currency}>
                            <Td className="font-mono text-mono-sm">{r.currency}</Td>
                            <Td align="end" className="tabular">{num(r.subscriptions)}</Td>
                            <Td align="end" className="tabular">{money(r.mrr, r.currency)}</Td>
                            <Td align="end" className="tabular">{money(r.arr, r.currency)}</Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                  {!recurringComplete && <p className="mt-2 text-caption text-warning-fg dark:text-warning-dark">{t('state.unconverted', { currencies: base.unconvertedCurrencies.join(', ') })}</p>}
                </Section>
                <Section title={t('finance.subs.trend')}>
                  <StartedCancelled rows={s.trend} />
                </Section>
              </div>

              <Section title={t('finance.subs.billing')} subtitle={<DayRange start={yearStart} last={d.asOf} />}>
                {s.billing.byCurrency.length === 0 ? (
                  <p className="text-body-sm text-muted">{t('finance.subs.noBilling')}</p>
                ) : (
                  <Table>
                    <thead>
                      <tr>
                        <Th>{t('common.currency')}</Th>
                        <Th align="end">{t('finance.invoiced')}</Th>
                        <Th align="end">{t('finance.collected')}</Th>
                        <Th align="end">{t('finance.outstandingNow')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.billing.byCurrency.map((b) => (
                        <Tr key={b.currency}>
                          <Td className="font-mono text-mono-sm">{b.currency}</Td>
                          <Td align="end" className="tabular">
                            {money(b.invoiced, b.currency)} <span className="text-caption text-muted">({t('finance.subs.invoices', { n: num(b.invoices) })})</span>
                          </Td>
                          <Td align="end" className="tabular">
                            {money(b.collected, b.currency)} <span className="text-caption text-muted">({t('finance.subs.payments', { n: num(b.payments) })})</span>
                          </Td>
                          <Td align="end" className="tabular">{money(b.outstanding, b.currency)}</Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                )}
                <p className="mt-2 text-caption text-muted">{t('finance.subs.collectedBase', { amount: money(s.billing.collectedBase.amount, s.billing.collectedBase.currency) })}</p>
              </Section>

              <div className="grid gap-4 xl:grid-cols-2">
                <Section title={t('finance.subs.aging')} subtitle={t('finance.subs.agingHint')}>
                  {s.aging.length === 0 ? (
                    <p className="text-body-sm text-muted">{t('finance.subs.nothingOwed')}</p>
                  ) : (
                    <Table>
                      <thead>
                        <tr>
                          <Th>{t('common.currency')}</Th>
                          {SUBSCRIPTION_AGING.map((k) => (
                            <Th key={k} align="end">
                              {t(`finance.subs.bucket.${k}`)}
                            </Th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {s.aging.map((row) => (
                          <Tr key={row.currency}>
                            <Td className="font-mono text-mono-sm">{row.currency}</Td>
                            {SUBSCRIPTION_AGING.map((k) => (
                              <Td key={k} align="end" className="tabular">
                                {money(row[k]?.amount ?? 0, row.currency)}
                                {(row[k]?.invoices ?? 0) > 0 && <span className="block text-caption text-muted">{t('finance.subs.invoices', { n: num(row[k]?.invoices ?? 0) })}</span>}
                              </Td>
                            ))}
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Section>
                <Section title={t('finance.subs.upcoming')} subtitle={t('finance.subs.upcomingHint')}>
                  {s.upcoming.length === 0 ? (
                    <p className="text-body-sm text-muted">{t('finance.subs.noUpcoming')}</p>
                  ) : (
                    <Table>
                      <thead>
                        <tr>
                          <Th>{t('finance.subs.subscription')}</Th>
                          <Th>{t('common.client')}</Th>
                          <Th>{t('finance.subs.nextBilling')}</Th>
                          <Th align="end">{t('finance.subs.price')}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.upcoming.map((u) => (
                          <Tr key={u.id}>
                            <Td>
                              <span className="font-mono text-mono-sm" dir="ltr">
                                {u.number}
                              </span>
                              {u.package && <span className="block text-caption text-muted">{localized(u.package)}</span>}
                            </Td>
                            <Td>{u.client ? localized(u.client) : '—'}</Td>
                            <Td className="whitespace-nowrap">
                              {day(u.nextBillingDate)}
                              <span className="block text-caption text-muted">{enumLabel(u.billingCycle, locale)}</span>
                            </Td>
                            <Td align="end" className="tabular">{money(u.netPrice, u.currency)}</Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Section>
              </div>
            </section>

            <section className="space-y-4" aria-labelledby="contracts-title">
              <h2 id="contracts-title" className="text-h2 text-[var(--text-primary)]">
                {t('finance.contracts.title')}
              </h2>
              <p className="flex items-start gap-2 text-body-sm text-muted">
                <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                {t('finance.contracts.faceValue')}
              </p>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <KpiCard label={t('finance.contracts.active')} value={num(c.active)} />
                <KpiCard label={t('finance.contracts.waiting')} value={num(c.waitingToStart)} />
                <KpiCard label={t('finance.contracts.expiring', { days: num(c.expiringWithinDays) })} value={num(c.expiring.length)} tone={c.expiring.length > 0 ? 'warning' : 'neutral'} />
                <KpiCard
                  label={t('finance.contracts.dueTotal')}
                  value={
                    <span className="text-title">
                      <CurrencyAmounts amounts={c.billing.dueNotInvoiced} empty={t('finance.expected.none')} />
                    </span>
                  }
                  hint={t('finance.contracts.dueHint')}
                  tone={c.billing.dueNotInvoiced.some((a) => a.amount > 0) ? 'warning' : 'neutral'}
                />
              </div>

              <Section title={t('finance.contracts.expiringList')}>
                {c.expiring.length === 0 ? (
                  <p className="text-body-sm text-muted">{t('finance.contracts.noneExpiring', { days: num(c.expiringWithinDays) })}</p>
                ) : (
                  <Table>
                    <thead>
                      <tr>
                        <Th>{t('finance.contracts.contract')}</Th>
                        <Th>{t('common.client')}</Th>
                        <Th>{t('common.project')}</Th>
                        <Th>{t('finance.contracts.ends')}</Th>
                        <Th align="end">{t('finance.contracts.daysLeft')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.expiring.map((x) => (
                        <Tr key={x.id}>
                          <Td>
                            <span className="font-mono text-mono-sm" dir="ltr">
                              {x.number}
                            </span>
                            <span className="block text-caption text-muted">{x.title}</span>
                          </Td>
                          <Td>
                            {x.client ? (
                              <Link to={hrefWith(`/clients/${x.client.id}`, query)} className="hover:underline">
                                {localized(x.client)}
                              </Link>
                            ) : (
                              '—'
                            )}
                          </Td>
                          <Td>
                            {x.project ? (
                              <Link to={hrefWith(`/projects/${x.project.id}`, query)} className="hover:underline">
                                {localized(x.project)}
                              </Link>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </Td>
                          <Td className="whitespace-nowrap">{day(x.endDate)}</Td>
                          <Td align="end" className="tabular">{num(x.daysLeft)}</Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Section>

              <Section title={t('finance.contracts.billing')} subtitle={t('finance.contracts.billingHint', { days: num(c.billing.horizonDays) })}>
                <div className="mb-4 grid gap-4 sm:grid-cols-2">
                  <Fact label={t('finance.contracts.dueTotal')}>
                    <CurrencyAmounts amounts={c.billing.dueNotInvoiced} empty={t('finance.expected.none')} />
                  </Fact>
                  <Fact label={t('finance.contracts.upcomingTotal', { days: num(c.billing.horizonDays) })}>
                    <CurrencyAmounts amounts={c.billing.upcoming} empty={t('finance.expected.none')} />
                  </Fact>
                </div>
                {c.billing.periods.length === 0 ? (
                  <p className="text-body-sm text-muted">{t('finance.contracts.noPeriods')}</p>
                ) : (
                  <Table>
                    <thead>
                      <tr>
                        <Th>{t('finance.contracts.contract')}</Th>
                        <Th>{t('common.client')}</Th>
                        <Th>{t('finance.contracts.period')}</Th>
                        <Th align="end">{t('common.amount')}</Th>
                        <Th>{t('common.status')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.billing.periods.map((p) => (
                        <Tr key={`${p.contractId}-${p.start}`}>
                          <Td>
                            <span className="font-mono text-mono-sm" dir="ltr">
                              {p.number}
                            </span>
                          </Td>
                          <Td>{p.client ? localized(p.client) : '—'}</Td>
                          <Td>
                            <DayRange start={p.start} last={p.end} />
                          </Td>
                          <Td align="end" className="tabular">{money(p.amount, p.currency)}</Td>
                          <Td>
                            <Badge tone={p.state === 'DUE' ? 'warning' : 'info'}>{p.state === 'DUE' ? t('finance.contracts.stateDue') : t('finance.contracts.stateUpcoming')}</Badge>
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Section>
            </section>
          </div>
        );
      }}
    </Loaded>
  );
}

// ---- trust & coverage ----------------------------------------------------------------------------------

const BASIS_KEYS = ['revenue', 'expenses', 'payroll', 'pointsCash', 'historical', 'balances'] as const;
const FX_KEYS = ['payments', 'historical', 'partners', 'expenses', 'balances'] as const;
const TRUST_STATUSES = ['VERIFIED', 'PREVIEW', 'UNVERIFIED', 'PARTIAL', 'NOT_RECONCILED', 'SETUP_NEEDED', 'UNAVAILABLE', 'RESTRICTED'] as const;

function TrustTab() {
  const { t, has, locale } = useI18n();
  const day = useDay();
  const monthName = useMonthName();
  const hint = useTrustHint();
  const num = useNumber();
  const money = useMoney();
  const q = useFinanceOverview();
  const word = (prefix: string, value: string | null | undefined) => {
    const key = `${prefix}${value ?? ''}`;
    return has(key) ? t(key) : (value ?? '—');
  };
  return (
    <Loaded query={q}>
      {(d) => {
        const ctx = d.context;
        const cutover = ctx.cutover;
        return (
          <div className="space-y-5">
            <Section title={t('finance.trust.statusTitle')}>
              <div className="flex flex-wrap items-center gap-2">
                <TrustBadge status={ctx.status} />
                <span className="text-body-sm text-muted">{hint(ctx.status)}</span>
              </div>
              {ctx.comparisonStatus && (
                <p className="mt-2 flex flex-wrap items-center gap-2 text-caption text-muted">
                  {t('finance.trust.comparison')}
                  <TrustBadge status={ctx.comparisonStatus} />
                </p>
              )}
              <dl className="mt-4 grid gap-4 sm:grid-cols-3">
                <Fact label={t('period.label')}>
                  <DayRange start={d.period.start} last={d.period.elapsed.last} />
                </Fact>
                <Fact label={t('finance.trust.generated')}>{ctx.generatedAt ? formatDateTime(ctx.generatedAt, locale) : '—'}</Fact>
                <Fact label={t('finance.trust.timezone')}>{ctx.timezone ?? d.period.timezone}</Fact>
              </dl>
              <details className="mt-4">
                <summary className="cursor-pointer select-none text-caption text-primary-600 dark:text-primary-400">{t('finance.trust.legend')}</summary>
                <ul className="mt-2 space-y-1.5">
                  {TRUST_STATUSES.map((s) => (
                    <li key={s} className="flex flex-wrap items-center gap-2 text-caption text-muted">
                      <TrustBadge status={s} />
                      {hint(s)}
                    </li>
                  ))}
                </ul>
              </details>
            </Section>

            <div className="grid gap-4 xl:grid-cols-2">
              <Section title={t('finance.trust.cutover')}>
                <dl className="grid gap-4 sm:grid-cols-2">
                  <Fact label={t('finance.trust.cutoverDate')}>{cutover?.date ? day(cutover.date) : t('common.none')}</Fact>
                  <Fact label={t('finance.trust.cutoverStatus')}>{word('finance.cutover.', cutover?.status)}</Fact>
                  <Fact label={t('finance.trust.confirmedAt')}>{cutover?.confirmedAt ? formatDateTime(cutover.confirmedAt, locale) : '—'}</Fact>
                  <Fact label={t('finance.trust.standing')}>{word('finance.standing.', d.standing)}</Fact>
                </dl>
              </Section>
              <Section title={t('finance.trust.cash')}>
                <div className="flex flex-wrap items-center gap-2">
                  <TrustBadge status={d.cards.cash.status} />
                  <span className="text-caption text-muted">{t('finance.asOfDate', { date: day(d.cards.cash.asOf) })}</span>
                </div>
                {d.cards.cash.byCurrency ? (
                  d.cards.cash.byCurrency.length === 0 ? (
                    <p className="mt-3 text-body-sm text-muted">{t('finance.cashNone')}</p>
                  ) : (
                    <Table className="mt-3">
                      <thead>
                        <tr>
                          <Th>{t('common.currency')}</Th>
                          <Th align="end">{t('common.amount')}</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.cards.cash.byCurrency.map((c) => (
                          <Tr key={c.currency}>
                            <Td className="font-mono text-mono-sm">{c.currency}</Td>
                            <Td align="end" className="tabular">{money(Number(c.amount), c.currency)}</Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  )
                ) : (
                  <p className="mt-3 text-body-sm text-muted">{hint(d.cards.cash.status)}</p>
                )}
                <p className="mt-2 text-caption text-[var(--text-tertiary)]">{t('finance.cashHint')}</p>
              </Section>
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <Section title={t('finance.trust.basis')}>
                <dl className="space-y-2">
                  {BASIS_KEYS.map((k) => (
                    <div key={k} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 text-body-sm">
                      <dt className="text-[var(--text-tertiary)]">{t(`finance.basis.${k}`)}</dt>
                      <dd className="text-end text-[var(--text-primary)]">{word('finance.basisValue.', ctx.basis?.[k])}</dd>
                    </div>
                  ))}
                </dl>
              </Section>
              <Section title={t('finance.trust.fx')}>
                <dl className="space-y-2">
                  {FX_KEYS.map((k) => (
                    <div key={k} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 text-body-sm">
                      <dt className="text-[var(--text-tertiary)]">{t(`finance.fx.${k}`)}</dt>
                      <dd className="text-end text-[var(--text-primary)]">{word('finance.fxValue.', ctx.fx?.[k])}</dd>
                    </div>
                  ))}
                </dl>
              </Section>
            </div>

            <Section title={t('finance.trust.notices')}>
              {ctx.notices.length ? <NoticeList notices={ctx.notices} /> : <p className="text-body-sm text-muted">{t('finance.trust.noNotices')}</p>}
            </Section>

            <Section title={t('finance.trust.monthCoverage')}>
              {d.months.length === 0 ? (
                <p className="text-body-sm text-muted">{t('state.noData')}</p>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('finance.month')}</Th>
                      <Th>{t('finance.trust.standing')}</Th>
                      <Th>{t('finance.collected')}</Th>
                      <Th>{t('finance.card.salaries')}</Th>
                      <Th>{t('finance.card.otherExpenses')}</Th>
                      <Th>{t('finance.result')}</Th>
                      <Th align="end">{t('finance.trust.drafts')}</Th>
                      <Th align="end">{t('finance.trust.missingDocuments')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.months.map((m) => (
                      <Tr key={m.month}>
                        <Td className="whitespace-nowrap">
                          {monthName(m.month)}
                          {m.coverage.partialMonth && <span className="block text-caption text-muted">{t('finance.partialMonth')}</span>}
                        </Td>
                        <Td className="text-caption text-muted">{word('finance.standing.', m.coverage.standing)}</Td>
                        <Td>
                          <CoverageBadge state={m.coverage.revenue} />
                        </Td>
                        <Td>
                          <CoverageBadge state={m.coverage.salaries} />
                        </Td>
                        <Td>
                          <CoverageBadge state={m.coverage.otherExpenses} />
                        </Td>
                        <Td>
                          <CoverageBadge state={m.coverage.profit} />
                        </Td>
                        <Td align="end" className="tabular">{num(m.coverage.drafts)}</Td>
                        <Td align="end" className="tabular">{num(m.coverage.missingDocuments)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Section>
            <p className="text-caption text-muted">{t('finance.readOnly')}</p>
          </div>
        );
      }}
    </Loaded>
  );
}
